import type { ClientId } from '../client/bridge-api-client-types';
import { emitter, type EventSubscription } from '../events/bridge-events-emitter';
import type {
	AnyBridgeEvent,
	ClientLifecycleEvent,
	QueueEvent,
} from '../events/bridge-events-types';
import type {
	BridgeObservabilityConfig,
	BridgeObservabilityEvent,
	BridgeObservabilitySink,
} from './bridge-observability-types';

const DEFAULT_CONFIG: Required<BridgeObservabilityConfig> = {
	registrationGapThresholdMs: 10_000,
	queueDepthWarningThreshold: 50,
	registrationGapDebounceMs: 60_000,
};

/**
 * Observes the bridge's internal domain events and forwards a curated set of health signals to a
 * host-provided sink, so BE→FE bridge failures can be surfaced by frontend metrics.
 *
 * The signal that matters most — a receiver whose iframe is mounted but which never registers in
 * the bridge, leaving its streamed messages to accumulate unconsumed — is derived here rather than
 * emitted by the bridge core: a receiver that has buffered messages but produces no
 * `client-registered` / `queue-flushed` within {@link BridgeObservabilityConfig.registrationGapThresholdMs}
 * yields a `registration-gap`.
 *
 * Mirrors {@link BridgeDebugConsumer}; the emitter is explicitly designed for external observers.
 */
export class BridgeObservabilityConsumer {
	private subscription: EventSubscription | null = null;
	private sink: BridgeObservabilitySink | null = null;
	private config: Required<BridgeObservabilityConfig> = { ...DEFAULT_CONFIG };

	private readonly registeredClients = new Set<ClientId>();
	private readonly pendingReceivers = new Map<
		ClientId,
		{ firstQueuedAt: number; timer: ReturnType<typeof setTimeout> }
	>();
	private readonly lastGapEmittedAt = new Map<ClientId, number>();

	enable(sink: BridgeObservabilitySink, config?: BridgeObservabilityConfig): void {
		if (this.subscription) {
			return;
		}
		if (config) {
			this.config = { ...DEFAULT_CONFIG, ...config };
		}
		this.sink = sink;
		this.subscription = emitter().onAny((event) => this.handle(event));
	}

	disable(): void {
		this.subscription?.unsubscribe();
		this.subscription = null;
		this.sink = null;
		for (const { timer } of this.pendingReceivers.values()) {
			clearTimeout(timer);
		}
		this.pendingReceivers.clear();
		this.registeredClients.clear();
		this.lastGapEmittedAt.clear();
	}

	isEnabled(): boolean {
		return this.subscription !== null;
	}

	private handle(event: AnyBridgeEvent): void {
		switch (event.type) {
			case 'client-registered':
			case 'client-discovered': {
				const { clientId } = event as ClientLifecycleEvent;
				this.registeredClients.add(clientId);
				this.resolvePendingReceiver(clientId, 'registration-succeeded');
				break;
			}
			case 'client-teardown': {
				const { clientId } = event as ClientLifecycleEvent;
				this.registeredClients.delete(clientId);
				this.resolvePendingReceiver(clientId, 'registration-aborted');
				break;
			}
			case 'message-queued': {
				const queueEvent = event as QueueEvent;
				const receiverId = queueEvent.clientId;
				if (receiverId && !this.registeredClients.has(receiverId)) {
					this.trackPendingReceiver(receiverId);
				}
				if ((queueEvent.messageCount ?? 0) >= this.config.queueDepthWarningThreshold) {
					this.forward('message-queued', event, {
						clientId: receiverId,
						reason: queueEvent.reason,
						queueDepth: queueEvent.messageCount,
					});
				}
				break;
			}
			case 'queue-flushed': {
				const receiverId = (event as QueueEvent).clientId;
				if (receiverId) {
					this.resolvePendingReceiver(receiverId, 'registration-succeeded');
				}
				break;
			}
			case 'queue-cleared': {
				const receiverId = (event as QueueEvent).clientId;
				if (receiverId) {
					this.resolvePendingReceiver(receiverId, 'registration-aborted');
				}
				break;
			}
			case 'message-dropped': {
				const queueEvent = event as QueueEvent;
				this.forward('message-dropped', event, {
					clientId: queueEvent.clientId,
					reason: queueEvent.reason,
					queueDepth: queueEvent.messageCount,
				});
				break;
			}
			default:
				break;
		}
	}

	private trackPendingReceiver(receiverId: ClientId): void {
		if (this.pendingReceivers.has(receiverId)) {
			return;
		}
		const timer = setTimeout(
			() => this.emitRegistrationGap(receiverId),
			this.config.registrationGapThresholdMs,
		);
		this.pendingReceivers.set(receiverId, { firstQueuedAt: Date.now(), timer });
		this.sink?.({
			signal: 'registration-pending',
			clientId: receiverId,
			timestamp: Date.now(),
		});
	}

	private resolvePendingReceiver(
		receiverId: ClientId,
		outcome: 'registration-succeeded' | 'registration-aborted',
	): void {
		const pending = this.pendingReceivers.get(receiverId);
		if (!pending) {
			return;
		}
		clearTimeout(pending.timer);
		this.pendingReceivers.delete(receiverId);

		const now = Date.now();
		this.sink?.({
			signal: outcome,
			clientId: receiverId,
			waitedMs: now - pending.firstQueuedAt,
			timestamp: now,
		});
	}

	private emitRegistrationGap(receiverId: ClientId): void {
		const pending = this.pendingReceivers.get(receiverId);
		this.pendingReceivers.delete(receiverId);
		if (!pending || this.registeredClients.has(receiverId)) {
			return;
		}

		const now = Date.now();
		const lastEmitted = this.lastGapEmittedAt.get(receiverId);
		if (lastEmitted !== undefined && now - lastEmitted < this.config.registrationGapDebounceMs) {
			return;
		}
		this.lastGapEmittedAt.set(receiverId, now);

		this.sink?.({
			signal: 'registration-gap',
			clientId: receiverId,
			waitedMs: now - pending.firstQueuedAt,
			timestamp: now,
		});
	}

	private forward(
		signal: BridgeObservabilityEvent['signal'],
		source: AnyBridgeEvent,
		fields: Partial<BridgeObservabilityEvent>,
	): void {
		this.sink?.({
			signal,
			timestamp: source.timestamp ?? Date.now(),
			bridgeId: source.bridgeId,
			...fields,
		});
	}
}

let globalObservabilityConsumer: BridgeObservabilityConsumer | null = null;

/**
 * Get or create the global bridge observability consumer.
 */
export function getObservabilityConsumer(): BridgeObservabilityConsumer {
	if (!globalObservabilityConsumer) {
		globalObservabilityConsumer = new BridgeObservabilityConsumer();
	}
	return globalObservabilityConsumer;
}

/**
 * Destroy the global observability consumer instance to prevent memory leaks.
 */
export function destroyGlobalObservabilityConsumer(): void {
	if (globalObservabilityConsumer) {
		globalObservabilityConsumer.disable();
		globalObservabilityConsumer = null;
	}
}
